import { useState, useCallback } from 'react'
import { imageService, ImageUploadOptions } from '@/utils/imageService'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'

interface UseImageUploadOptions {
    onUploadSuccess?: (imageData: any) => void
    onUploadError?: (error: string) => void
    showToastMessages?: boolean
}

export const useImageUpload = (options: UseImageUploadOptions = {}) => {
    const [uploading, setUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [uploadedImages, setUploadedImages] = useState<any[]>([])

    const { showSuccess, showError } = useSnackbarContext()

    const {
        onUploadSuccess,
        onUploadError,
        showToastMessages = true
    } = options

    const uploadSingleImage = useCallback(async (
        file: File,
        uploadOptions: ImageUploadOptions = {}
    ) => {
        try {
            setUploading(true)
            setUploadProgress(0)

            // Validate file
            const validation = imageService.validateImageFile(file)
            if (!validation.valid) {
                throw new Error(validation.error)
            }

            // Simulate progress (since we don't have real progress tracking)
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => Math.min(prev + 10, 90))
            }, 100)

            const response = await imageService.uploadImage(file, uploadOptions)

            clearInterval(progressInterval)
            setUploadProgress(100)

            const imageData = response.data.image
            setUploadedImages(prev => [...prev, imageData])

            if (showToastMessages) {
                showSuccess('Image uploaded successfully!')
            }

            onUploadSuccess?.(imageData)
            return imageData

        } catch (error: any) {
            const errorMessage = error.message || 'Failed to upload image'

            if (showToastMessages) {
                showError(errorMessage)
            }

            onUploadError?.(errorMessage)
            throw error

        } finally {
            setUploading(false)
            setTimeout(() => setUploadProgress(0), 1000)
        }
    }, [onUploadSuccess, onUploadError, showToastMessages])

    const uploadMultipleImages = useCallback(async (
        files: File[],
        uploadOptions: ImageUploadOptions = {}
    ) => {
        try {
            setUploading(true)
            setUploadProgress(0)

            // Validate all files
            for (const file of files) {
                const validation = imageService.validateImageFile(file)
                if (!validation.valid) {
                    throw new Error(`${file.name}: ${validation.error}`)
                }
            }

            // Simulate progress
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => Math.min(prev + 5, 90))
            }, 200)

            const response = await imageService.uploadMultipleImages(files, uploadOptions)

            clearInterval(progressInterval)
            setUploadProgress(100)

            const images = response.data.images
            setUploadedImages(prev => [...prev, ...images])

            if (showToastMessages) {
                showSuccess(`${images.length} images uploaded successfully!`)
            }

            onUploadSuccess?.(images)
            return images

        } catch (error: any) {
            const errorMessage = error.message || 'Failed to upload images'

            if (showToastMessages) {
                showError(errorMessage)
            }

            onUploadError?.(errorMessage)
            throw error

        } finally {
            setUploading(false)
            setTimeout(() => setUploadProgress(0), 1000)
        }
    }, [onUploadSuccess, onUploadError, showToastMessages])

    const uploadAvatar = useCallback(async (file: File) => {
        return uploadSingleImage(file, {
            imageType: 'avatar',
            imageName: `avatar_${Date.now()}`,
            quality: 90,
            maxWidth: 512,
            maxHeight: 512,
            forceFormat: 'jpeg'
        })
    }, [uploadSingleImage])

    const uploadGigImages = useCallback(async (files: File[], gigId: string) => {
        return uploadMultipleImages(files, {
            imageType: 'gig',
            referenceId: gigId,
            quality: 85,
            maxWidth: 1200,
            maxHeight: 900
        })
    }, [uploadMultipleImages])

    const clearUploadedImages = useCallback(() => {
        setUploadedImages([])
    }, [])

    const removeUploadedImage = useCallback((imageId: string) => {
        setUploadedImages(prev => prev.filter(img => img._id !== imageId))
    }, [])

    return {
        // State
        uploading,
        uploadProgress,
        uploadedImages,

        // Actions
        uploadSingleImage,
        uploadMultipleImages,
        uploadAvatar,
        uploadGigImages,
        clearUploadedImages,
        removeUploadedImage,

        // Utils
        validateFile: (file: File) => imageService.validateImageFile(file),
        formatFileSize: (bytes: number) => imageService.formatFileSize(bytes),
        getImageDimensions: (file: File) => imageService.getImageDimensions(file)
    }
}

export default useImageUpload